import * as TooltipPrimitive from "@radix-ui/react-tooltip";
import { HelpCircle } from "lucide-react";
import type { ReactNode } from "react";

import { isMobile } from "@krea/shared/utils/isMobile";
import {
  ResponsiveDialog,
  ResponsiveDialogContent,
  ResponsiveDialogTrigger,
} from "@krea/shared/design-system/modal/base-modal";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@krea/shared/design-system/Tooltip";
import { cn } from "@krea/shared/utils";

export interface IResponsiveTooltipProps {
  /** When interactive === false, the element that opens the tooltip/drawer (hover on desktop, click on mobile). Required when interactive is false. */
  trigger?: ReactNode;
  /** When interactive === false: tooltip/drawer content. When interactive === true: the main interactive element (e.g. button); tooltip/drawer content then comes from content. */
  children: ReactNode;
  /** Default false. When true: desktop = only children with tooltip on hover (no ? icon). Mobile = children + ? icon; ? icon is click trigger for bottom drawer. */
  interactive?: boolean;
  /** Optional class name applied to the tooltip content (desktop) and bottom drawer body (mobile). */
  className?: string;
  /** Placement of the content. Default: "bottom". */
  side?: "top" | "right" | "bottom" | "left";
  /** Horizontal alignment (tooltip only). Default: "start". */
  align?: "start" | "center" | "end";
  /** Offset in pixels from the trigger (tooltip only). Default: 4. */
  sideOffset?: number;
  /** When true, render only the trigger (or only children when interactive); no tooltip or dialog. */
  hidden?: boolean;
  /** Default true. When true, tooltip/drawer stack above modals/drawers (higher z-index). */
  elevatedStacking?: boolean;
  /** When true, render only on desktop (hover) */
  desktopOnly?: boolean;
}

const ELEVATED_Z_CLASS = "z-[500001]";

/**
 * Shows content in a tooltip on desktop (hover) and in a full-width bottom drawer on mobile (click).
 * Uses 768px as the breakpoint (matches Tailwind md).
 * When interactive: desktop = tooltip on children only (no ? icon); mobile = children + ? icon, ? icon opens drawer.
 */
export const ResponsiveTooltip = ({
  trigger,
  children,
  interactive = false,
  className,
  side = "top",
  align = "center",
  sideOffset = 4,
  hidden = false,
  elevatedStacking = true,
  desktopOnly = false,
}: IResponsiveTooltipProps) => {
  const isMobileCheck = isMobile();
  const mergedTooltipClassName = cn(
    className,
    elevatedStacking && ELEVATED_Z_CLASS,
  );
  const mergedDrawerClassName = cn(
    className,
    elevatedStacking && ELEVATED_Z_CLASS,
    "w-full max-w-[100vw]",
  );

  if (hidden || (isMobileCheck && desktopOnly)) {
    return trigger;
  }

  if (isMobileCheck && !desktopOnly) {
    if (interactive) {
      return (
        <ResponsiveDialog>
          <div className="flex items-center">
            {trigger}
            <ResponsiveDialogTrigger asChild>
              <span className="inline-flex cursor-pointer items-center justify-center rounded-full text-gray-500 hover:text-gray-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2">
                <HelpCircle className="size-4" />
              </span>
            </ResponsiveDialogTrigger>
          </div>
          <ResponsiveDialogContent
            side="bottom"
            className={mergedDrawerClassName}
            elevatedStacking={elevatedStacking}
          >
            {children}
          </ResponsiveDialogContent>
        </ResponsiveDialog>
      );
    }

    return (
      <ResponsiveDialog>
        <ResponsiveDialogTrigger asChild>{trigger}</ResponsiveDialogTrigger>
        <ResponsiveDialogContent
          side="bottom"
          className={mergedDrawerClassName}
          elevatedStacking={elevatedStacking}
        >
          {children}
        </ResponsiveDialogContent>
      </ResponsiveDialog>
    );
  }

  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>{trigger}</TooltipTrigger>
        <TooltipPrimitive.Portal>
          <TooltipContent
            side={side}
            align={align}
            sideOffset={sideOffset}
            className={mergedTooltipClassName}
          >
            {children}
          </TooltipContent>
        </TooltipPrimitive.Portal>
      </Tooltip>
    </TooltipProvider>
  );
};
